import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import CaseItemList from './CaseItemList';
import DrawerComponent from './Drawer';
import { Button } from '../../../component/Button';
import CaseItemForm from './CaseItemForm';
import Empty from '../../../component/Empty';
import { getCasePayData, createCasePay, editCasePay, deleteCasePay } from '../utils/handleCasePayData';
import { editingCasePay, resetForm, selectCasePayItem, resetDeleteForm, getDeleteTarget } from '../slice/casePaySlice';
import { messageSuccess, messageError, messageWarning } from '@/component/Message';
import DeleteModal from './DeleteModal';
import { filterBySearch } from '../utils/filterBySearch';
import { sortBySelect } from '../utils/sortBySelect';
import CustomizeModal from '../../../component/Modal';
import {
  resetUsedList,
  setClientError,
  setDuplicateName,
  setUsedCaseModalVisible,
} from '../../HomePage/slice/errorCenterSlice';
import { EllipsisText } from '../../../component/Display';
import Validate from '../../../models/validate';
import Spin from '../../../component/Spin';

//#region
export const EmptyContainer = styled.div`
  height: 100%;
`;
//#endregion

const casePaySelector = state => state.settingPageReducer.casePay;
const errorSelector = state => state.homePageReducer.errorCenter;
const wsSelector = state => state.homePageReducer.ws;
const casePayReselector = createSelector(casePaySelector, errorSelector, wsSelector, (pay, error, ws) => ({
  loading: pay.loading,
  payment: pay.casePay,
  editing: pay.editing,
  deleting: pay.deleting,
  errorMsgData: error.usedList,
  duplicateError: error.duplicateName,
  serverError: error.serverError,
  clientError: error.clientError,
  usedCaseModalVisible: error.usedCaseModalVisible,
  clinicId: ws?.ws?._id,
}));

const CasePayContent = ({ drawerVisible, handleDrawerClose, handleVisible, sort }) => {
  const { formatMessage: f } = useIntl();
  const dispatch = useDispatch();
  const {
    loading,
    payment,
    editing,
    deleting,
    errorMsgData,
    duplicateError,
    serverError,
    clientError,
    usedCaseModalVisible,
    clinicId,
  } = useSelector(casePayReselector);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [inputFilter, setInputFilter] = useState('');
  const [filters, setFilters] = useState(['activate', 'isArchived']);
  const [archivedWarning, setArchivedWarning] = useState({
    visible: false,
    name: '',
    id: '',
  });
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setValue,
  } = useForm();

  useEffect(() => {
    dispatch(getCasePayData());
  }, [dispatch]);

  useEffect(() => {
    if (clientError) {
      messageError(f({ id: 'message.error' }));
      dispatch(setClientError(false));
    }
  }, [f, clientError, dispatch]);

  const btnText = !_.isEmpty(editing._id) ? 'button.save' : 'button.add';
  const title = !_.isEmpty(editing._id)
    ? f({ id: 'caseType.editCasePayTitle' })
    : f({ id: 'caseType.addCasePayTitle' });

  const handleClose = () => {
    handleDrawerClose();
    reset({ name: '' });
    dispatch(resetForm());
    dispatch(setDuplicateName(false));
  };

  const submitHandler = async editing => {
    if (_.isEmpty(editing._id)) {
      await createCasePay({ name: editing.name.trim(), isCommonly: editing.isCommonly }, () => {
        messageSuccess(f({ id: 'message.add.success' }));
        handleClose();
        dispatch(getCasePayData());
      });
    } else {
      dispatch(
        editCasePay({ ...editing, name: editing.name.trim() }, () => {
          messageSuccess(f({ id: 'message.success' }));
          handleClose();
        }),
      );
    }
  };

  const onEditPayItem = item => {
    dispatch(selectCasePayItem(item));
    setValue('name', item.name);
    handleVisible();
  };

  const archivePayItem = id => {
    dispatch(
      editCasePay({ _id: id, isArchived: true, isCommonly: false }, () =>
        messageSuccess(f({ id: 'case.pay.archived.message' })),
      ),
    );
    setArchivedWarning({ id: '', name: '', visible: false });
  };

  const activatedPayItem = id => {
    dispatch(editCasePay({ _id: id, isArchived: false }, () => messageSuccess(f({ id: 'case.pay.activate.message' }))));
  };

  const switchCommonlyMark = (id, currentStatus) => {
    dispatch(editCasePay({ _id: id, isCommonly: !currentStatus }, () => messageSuccess(f({ id: 'message.success' }))));
  };

  const deletePayItem = (data, deleting) => {
    const successCallback = () => {
      reset({ reason: '', person: '' });
      dispatch(resetDeleteForm());
      dispatch(getCasePayData())
      messageWarning(f({ id: 'message.deleted' }));
    };
    dispatch(
      deleteCasePay(
        { _id: deleting._id, isDeleted: true, deletedReason: data.reason, deletedOperator: data.person },
        successCallback,
      ),
    );
    setIsModalVisible(false);
  };

  const showModal = data => {
    dispatch(getDeleteTarget(data));
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    dispatch(resetDeleteForm());
  };

  const showWarningModal = (id, name) =>
    setArchivedWarning({
      id,
      name,
      visible: true,
    });

  const deleteValidation = target => {
    Validate.deleteValidation({ _id: target._id, target: 'paymentMethod' }).then(res => {
      if (!!res) {
        showModal(target);
      }
    });
  };

  if (loading) {
    return <Spin />;
  }
  if (_.isEmpty(payment)) {
    return (
      <EmptyContainer>
        <Empty description={'labSetting.casePay.empty'} />
        <DrawerComponent
          handleClose={handleClose}
          visible={drawerVisible}
          title={f({ id: 'caseType.addCasePayTitle' })}
          footer={
            <Button
              type='primary'
              width={'150px'}
              size={'large'}
              onClick={handleSubmit(() => submitHandler(editing))}
              buttonText={'button.add'}
            />
          }
          children={
            <CaseItemForm
              control={control}
              error={errors}
              changeName={value => {
                dispatch(editingCasePay({ name: value }));
                dispatch(setDuplicateName(false));
              }}
              changeCommonly={e => dispatch(editingCasePay({ isCommonly: e.target.checked }))}
              value={editing}
              validate={duplicateError}
            />
          }
        />
      </EmptyContainer>
    );
  }
  return (
    <React.Fragment>
      <CaseItemList
        dataSource={sortBySelect({ source: filterBySearch(payment, inputFilter), filters, sort })}
        title='casePay.filter.title'
        handleEdit={onEditPayItem}
        handleArchived={showWarningModal}
        handleActivated={activatedPayItem}
        handleCommonly={switchCommonlyMark}
        handleDelete={deleteValidation}
        filterValue={inputFilter}
        handleInputFilter={setInputFilter}
        filters={filters}
        handleFilter={setFilters}
        sort={sort}
        emptyTitle={'search.empty'}
      />
      <DrawerComponent
        handleClose={handleClose}
        visible={drawerVisible}
        title={title}
        footer={
          <Button
            type='primary'
            width={'150px'}
            size={'large'}
            onClick={handleSubmit(() => submitHandler(editing))}
            buttonText={btnText}
          />
        }
        children={
          <CaseItemForm
            control={control}
            error={errors}
            changeName={value => {
              dispatch(editingCasePay({ name: value }));
              dispatch(setDuplicateName(false));
            }}
            changeCommonly={e => dispatch(editingCasePay({ isCommonly: e.target.checked }))}
            value={editing}
            validate={duplicateError}
          />
        }
      />
      <DeleteModal
        deleteTitle={'modal.delete.title.casePay'}
        deleteName={deleting.name}
        deleteModalVisible={isModalVisible}
        deleteData={deleting}
        handleDelete={data => deletePayItem(data, deleting)}
        handleCancel={handleCancel}
        failModalVisible={usedCaseModalVisible}
        handleModalFailVisible={() => {
          dispatch(setUsedCaseModalVisible(false));
          dispatch(resetUsedList());
        }}
        deleteFailedTitle={'modal.delete.fail.casePay.title'}
        deleteFailedDescription={'modal.delete.fail.description'}
        usedList={errorMsgData}
        serverError={serverError}
        clinicId={clinicId}
      />
      <CustomizeModal
        title={
          <EllipsisText>
            <EllipsisText.Normal>{f({ id: 'labSetting.archived.casePay' })}</EllipsisText.Normal>
            <EllipsisText.Ellipsis>{archivedWarning.name}</EllipsisText.Ellipsis>
            <EllipsisText.Normal>?</EllipsisText.Normal>
          </EllipsisText>
        }
        type={'warning'}
        visible={archivedWarning.visible}
        content={f({ id: 'lab.modal.warning.content' })}
        showCloseIcon={false}
        className={'wide'}
        handleCancel={() => setArchivedWarning({ id: '', name: '', visible: false })}
        handleOk={() => archivePayItem(archivedWarning.id)}
        footer={
          <div>
            <Button
              buttonText='labSetting.table.filterByArchived'
              type={'warning'}
              width={'82px'}
              size={'large'}
              onClick={() => archivePayItem(archivedWarning.id)}
            />
            <Button
              buttonText='button.cancel'
              type={'ghost-basic'}
              width={'82px'}
              size={'large'}
              onClick={() => setArchivedWarning({ id: '', name: '', visible: false })}
            />
          </div>
        }
      />
    </React.Fragment>
  );
};

export default CasePayContent;
